import React, { useRef, useEffect } from 'react';
import { Shield, Search, FileCheck, RefreshCw, Lock, AlertTriangle, FileText, BarChart3, CheckCircle, ArrowRight, X } from 'lucide-react';
import GridDecoration from '../components/GridDecoration';
import { useModals } from '../components/Layout';
import { AnimatedCounter, AnimatedProgressBar } from '../components/AnimatedStats';

const Landing = () => {
  const { setIsAuditModalOpen, setIsFrameworkModalOpen, setIsResearcherModalOpen } = useModals();
  const videoRef = useRef(null);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.playbackRate = 0.5;
    }
  }, []);

  return (
    <div className="landing-page">
      <main>
        {/* Hero Section */}
        <section className="hero-section">
          <video
            ref={videoRef}
            className="hero-video"
            autoPlay
            loop
            muted
            playsInline
          >
            <source src={`${process.env.PUBLIC_URL}/adj.mp4`} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
          <div className="hero-glass-overlay"></div>

          {/* Top 45-50%: Text Only */}
          <div className="hero-text-zone">
            <div className="hero-text-content">
              <h1 className="hero-title">Security for AI. Security from AI.</h1>
              <p className="hero-subtitle">
                Adjactive audits, tests, and hardens AI models against exploitation, misuse, and regulatory risk.
              </p>
              <div className="hero-buttons">
                <button onClick={() => setIsAuditModalOpen(true)} className="btn-primary">
                  Request AI Audit
                  <ArrowRight size={20} />
                </button>
                <button onClick={() => setIsFrameworkModalOpen(true)} className="btn-secondary">
                  View Framework
                </button>
              </div>
            </div>
          </div>

          {/* Bottom 50-55%: Visual Only */}
          <div className="hero-visual-zone">
          </div>
        </section>

        {/* Trust Strip with Stats */}
        <section className="trust-strip">
          <div className="trust-stat">
            <div className="stat-number">45+</div>
            <div className="stat-label">AI Models Audited</div>
          </div>
          <div className="trust-stat">
            <div className="stat-number">50+</div>
            <div className="stat-label">Enterprise Customers</div>
          </div>
          <div className="trust-stat">
            <div className="stat-number">3+</div>
            <div className="stat-label">Global AI Standards Supported</div>
          </div>
          <div className="trust-stat">
            <div className="stat-number">24/7</div>
            <div className="stat-label">Deep Red Teaming</div>
          </div>
        </section>

        {/* Problem Section */}
        <section className="problem-section">
          <h2 className="section-title">AI Without Security Is a Liability</h2>
          <div className="problem-grid">
            <div className="problem-card">
              <div className="card-icon-wrapper">
                <AlertTriangle size={32} />
              </div>
              <h3 className="card-title">Model Exploitation</h3>
              <ul className="card-list">
                <li>Prompt injection attacks</li>
                <li>Model extraction & theft</li>
                <li>Inference abuse</li>
                <li>Data poisoning</li>
              </ul>
            </div>

            <div className="problem-card">
              <div className="card-icon-wrapper">
                <FileText size={32} />
              </div>
              <h3 className="card-title">Governance Gaps</h3>
              <ul className="card-list">
                <li>No audit trails</li>
                <li>No explainability</li>
                <li>No accountability</li>
                <li>Compliance blind spots</li>
              </ul>
            </div>

            <div className="problem-card">
              <div className="card-icon-wrapper">
                <Lock size={32} />
              </div>
              <h3 className="card-title">Uncontrolled Impact</h3>
              <ul className="card-list">
                <li>Harmful outputs</li>
                <li>Bias & discrimination</li>
                <li>Autonomous decision risk</li>
                <li>Reputational damage</li>
              </ul>
            </div>
          </div>
        </section>

        {/* What is Adjactive */}
        <section className="what-section" id="framework">
          <h2 className="section-title">What is Adjactive</h2>
          <p className="section-description">
            A structured AI security and audit framework covering the entire AI lifecycle — from training data to deployment and real-world impact.
          </p>

          <div className="lifecycle-diagram">
            <div className="lifecycle-step">
              <div className="step-icon">Data</div>
              <div className="step-arrow">→</div>
            </div>
            <div className="lifecycle-step">
              <div className="step-icon">Model</div>
              <div className="step-arrow">→</div>
            </div>
            <div className="lifecycle-step">
              <div className="step-icon">API</div>
              <div className="step-arrow">→</div>
            </div>
            <div className="lifecycle-step">
              <div className="step-icon">User</div>
              <div className="step-arrow">→</div>
            </div>
            <div className="lifecycle-step">
              <div className="step-icon">Impact</div>
            </div>
          </div>

          <div className="lifecycle-overlay">
            <div className="overlay-badge">Security</div>
            <div className="overlay-badge">Bias Detection</div>
            <div className="overlay-badge">Governance</div>
          </div>
        </section>

        {/* Core Capabilities */}
        <section className="capabilities-section">
          <h2 className="section-title">Core Capabilities</h2>
          <div className="capabilities-grid">
            <div className="capability-card">
              <Search size={40} className="capability-icon" />
              <h3 className="card-title">AI Model Auditing</h3>
              <ul className="capability-list">
                <li>Comprehensive model assessment</li>
                <li>Vulnerability identification</li>
                <li>Risk scoring & classification</li>
                <li>Compliance verification</li>
              </ul>
            </div>

            <div className="capability-card">
              <Shield size={40} className="capability-icon" />
              <h3 className="card-title">AI Security Pentesting</h3>
              <ul className="capability-list">
                <li>Adversarial attack simulation</li>
                <li>Prompt injection testing</li>
                <li>Model extraction attempts</li>
                <li>API security assessment</li>
              </ul>
            </div>

            <div className="capability-card">
              <FileCheck size={40} className="capability-icon" />
              <h3 className="card-title">AI Governance & Compliance</h3>
              <ul className="capability-list">
                <li>Regulatory alignment (EU AI Act, etc.)</li>
                <li>Policy framework development</li>
                <li>Audit trail implementation</li>
                <li>Explainability mechanisms</li>
              </ul>
            </div>

            <div className="capability-card">
              <RefreshCw size={40} className="capability-icon" />
              <h3 className="card-title">Continuous Assurance</h3>
              <ul className="capability-list">
                <li>Ongoing monitoring</li>
                <li>Drift detection</li>
                <li>Regular re-assessment</li>
                <li>Incident response protocols</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Signature Differentiator */}
        <section className="differentiator-section">
          <h2 className="section-title">Our Approach</h2>
          <div className="differentiator-grid">
            <div className="differentiator-card">
              <div className="diff-header">
                <Shield size={32} />
                <h3 className="card-title">Security FOR AI</h3>
              </div>
              <p className="diff-description">Protect models from attackers</p>
              <ul className="diff-list">
                <li>Secure inference & APIs</li>
                <li>Prevent data leakage</li>
                <li>Model integrity protection</li>
                <li>Access control mechanisms</li>
              </ul>
            </div>

            <div className="differentiator-card">
              <div className="diff-header">
                <Lock size={32} />
                <h3 className="card-title">Security FROM AI</h3>
              </div>
              <p className="diff-description">Prevent harmful outputs</p>
              <ul className="diff-list">
                <li>Control misuse scenarios</li>
                <li>Reduce regulatory exposure</li>
                <li>Bias & fairness testing</li>
                <li>Output filtering & monitoring</li>
              </ul>
            </div>
          </div>
        </section>

        {/* How It Works - Timeline Style */}
        <section className="how-section" id="research">
          <h2 className="section-title">The AI Security Audit Lifecycle</h2>
          <p className="section-description">
            A comprehensive, structured approach to identifying and mitigating AI security risks
          </p>

          <div className="timeline-container">
            <div className="timeline-item">
              <div className="timeline-marker">
                <div className="timeline-dot">01</div>
                <div className="timeline-line"></div>
              </div>
              <div className="timeline-content">
                <h3 className="timeline-title">Scope & Threat Modeling</h3>
                <p className="timeline-description">Define AI system boundaries, data flows, and identify potential attack vectors specific to your implementation</p>
                <div className="timeline-duration">Duration: 2-3 days</div>
              </div>
            </div>

            <div className="timeline-item">
              <div className="timeline-marker">
                <div className="timeline-dot">02</div>
                <div className="timeline-line"></div>
              </div>
              <div className="timeline-content">
                <h3 className="timeline-title">Automated + Manual Testing</h3>
                <p className="timeline-description">Combined approach using proprietary tools and expert security analysis to simulate real-world attacks</p>
                <div className="timeline-duration">Duration: 5-7 days</div>
              </div>
            </div>

            <div className="timeline-item">
              <div className="timeline-marker">
                <div className="timeline-dot">03</div>
                <div className="timeline-line"></div>
              </div>
              <div className="timeline-content">
                <h3 className="timeline-title">Risk Scoring & Findings</h3>
                <p className="timeline-description">Detailed vulnerability report with CVSS-based severity classification and exploitability assessment</p>
                <div className="timeline-duration">Duration: 1-2 days</div>
              </div>
            </div>

            <div className="timeline-item">
              <div className="timeline-marker">
                <div className="timeline-dot">04</div>
                <div className="timeline-line"></div>
              </div>
              <div className="timeline-content">
                <h3 className="timeline-title">Remediation Playbooks</h3>
                <p className="timeline-description">Actionable, step-by-step guidance with code examples and configuration recommendations</p>
                <div className="timeline-duration">Duration: Ongoing support</div>
              </div>
            </div>

            <div className="timeline-item">
              <div className="timeline-marker">
                <div className="timeline-dot">05</div>
              </div>
              <div className="timeline-content">
                <h3 className="timeline-title">Re-testing & Continuous Assurance</h3>
                <p className="timeline-description">Validation of fixes and ongoing monitoring recommendations to ensure long-term security</p>
                <div className="timeline-duration">Duration: Post-remediation</div>
              </div>
            </div>
          </div>
        </section>

        {/* Sample Output */}
        <section className="sample-section">
          <h2 className="section-title">Sample Audit Output</h2>
          <p className="section-description">
            Real-world examples of vulnerability findings and compliance metrics from our audits
          </p>
          <div className="sample-dashboard">
            <div className="dashboard-card">
              <div className="dashboard-header">
                <BarChart3 size={24} />
                <h4>Risk Severity Distribution</h4>
              </div>
              <div className="risk-bars">
                <div className="risk-bar critical">
                  <span className="risk-label">Critical</span>
                  <AnimatedProgressBar width="15%" className="risk-progress-container" fillClassName="risk-fill critical-fill" />
                  <span className="risk-count">
                    <AnimatedCounter target="3" />
                  </span>
                </div>
                <div className="risk-bar high">
                  <span className="risk-label">High</span>
                  <AnimatedProgressBar width="35%" className="risk-progress-container" fillClassName="risk-fill high-fill" />
                  <span className="risk-count">
                    <AnimatedCounter target="7" />
                  </span>
                </div>
                <div className="risk-bar medium">
                  <span className="risk-label">Medium</span>
                  <AnimatedProgressBar width="45%" className="risk-progress-container" fillClassName="risk-fill medium-fill" />
                  <span className="risk-count">
                    <AnimatedCounter target="9" />
                  </span>
                </div>
                <div className="risk-bar low">
                  <span className="risk-label">Low</span>
                  <AnimatedProgressBar width="25%" className="risk-progress-container" fillClassName="risk-fill low-fill" />
                  <span className="risk-count">
                    <AnimatedCounter target="5" />
                  </span>
                </div>
              </div>
            </div>

            <div className="dashboard-card">
              <div className="dashboard-header">
                <CheckCircle size={24} />
                <h4>Compliance Readiness</h4>
              </div>
              <div className="compliance-meter">
                <div className="meter-value">
                  <AnimatedCounter target="100" suffix="%" />
                </div>
                <div className="meter-label">Compliant</div>
                <AnimatedProgressBar width="100%" className="meter-bar" fillClassName="meter-fill" />
                <p className="meter-note">EU AI Act alignment assessed</p>
              </div>
            </div>

            <div className="dashboard-card findings-card">
              <div className="dashboard-header">
                <AlertTriangle size={24} />
                <h4>Key Findings</h4>
              </div>
              <ul className="findings-list">
                <li className="finding-item critical-finding">
                  <span className="finding-severity">CRITICAL</span>
                  <span className="finding-text">Prompt injection vulnerability detected</span>
                </li>
                <li className="finding-item high-finding">
                  <span className="finding-severity">HIGH</span>
                  <span className="finding-text">Model inference API lacks rate limiting</span>
                </li>
                <li className="finding-item high-finding">
                  <span className="finding-severity">HIGH</span>
                  <span className="finding-text">No audit logging for model decisions</span>
                </li>
                <li className="finding-item medium-finding">
                  <span className="finding-severity">MEDIUM</span>
                  <span className="finding-text">Bias detected in demographic classifications</span>
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* Comparison Table */}
        <section className="comparison-section">
          <h2 className="section-title">Adjactive vs Traditional Security Audits</h2>
          <p className="section-description">
            See how our AI-first approach outperforms traditional application security testing
          </p>

          <div className="comparison-table">
            <div className="comparison-row comparison-header">
              <div className="comparison-cell feature-cell">
                <span className="feature-label">Feature</span>
              </div>
              <div className="comparison-cell adjactive-cell">
                <span className="provider-label">Adjactive</span>
              </div>
              <div className="comparison-cell traditional-cell">
                <span className="provider-label">Traditional Audits</span>
              </div>
            </div>

            <div className="comparison-row">
              <div className="comparison-cell feature-cell">
                <strong>AI-Specific Testing</strong>
              </div>
              <div className="comparison-cell adjactive-cell">
                <CheckCircle size={20} className="check-icon" />
                <span>Prompt injection, model extraction, bias testing</span>
              </div>
              <div className="comparison-cell traditional-cell">
                <X size={20} className="x-icon" />
                <span>Generic web/API testing only</span>
              </div>
            </div>

            <div className="comparison-row">
              <div className="comparison-cell feature-cell">
                <strong>Coverage Scope</strong>
              </div>
              <div className="comparison-cell adjactive-cell">
                <CheckCircle size={20} className="check-icon" />
                <span>Training data → Deployment → Impact</span>
              </div>
              <div className="comparison-cell traditional-cell">
                <X size={20} className="x-icon" />
                <span>Application layer only</span>
              </div>
            </div>

            <div className="comparison-row">
              <div className="comparison-cell feature-cell">
                <strong>Governance & Compliance</strong>
              </div>
              <div className="comparison-cell adjactive-cell">
                <CheckCircle size={20} className="check-icon" />
                <span>EU AI Act, NIST AI RMF alignment</span>
              </div>
              <div className="comparison-cell traditional-cell">
                <X size={20} className="x-icon" />
                <span>Generic compliance frameworks</span>
              </div>
            </div>

            <div className="comparison-row">
              <div className="comparison-cell feature-cell">
                <strong>Time to Detection</strong>
              </div>
              <div className="comparison-cell adjactive-cell">
                <CheckCircle size={20} className="check-icon" />
                <span>Real-time validation during audit</span>
              </div>
              <div className="comparison-cell traditional-cell">
                <X size={20} className="x-icon" />
                <span>Post-audit manual validation</span>
              </div>
            </div>

            <div className="comparison-row">
              <div className="comparison-cell feature-cell">
                <strong>Explainability</strong>
              </div>
              <div className="comparison-cell adjactive-cell">
                <CheckCircle size={20} className="check-icon" />
                <span>Model decision tracing & transparency</span>
              </div>
              <div className="comparison-cell traditional-cell">
                <X size={20} className="x-icon" />
                <span>Not assessed</span>
              </div>
            </div>

            <div className="comparison-row">
              <div className="comparison-cell feature-cell">
                <strong>Remediation Support</strong>
              </div>
              <div className="comparison-cell adjactive-cell">
                <CheckCircle size={20} className="check-icon" />
                <span>Code-level fixes & re-testing included</span>
              </div>
              <div className="comparison-cell traditional-cell">
                <X size={20} className="x-icon" />
                <span>Report delivery only</span>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="final-cta-section" id="contact">
          <h2 className="cta-title">Make Your AI Defensible, Compliant, and Trustworthy</h2>
          <p className="cta-subtitle">Partner with AI security experts to protect your organization</p>
          <div className="cta-buttons">
            <button onClick={() => setIsAuditModalOpen(true)} className="btn-primary btn-large">
              Request AI Audit
              <ArrowRight size={24} />
            </button>
            <button onClick={() => setIsResearcherModalOpen(true)} className="btn-secondary btn-large">
              Talk to a Researcher
            </button>
            <button onClick={() => setIsFrameworkModalOpen(true)} className="btn-secondary btn-large">
              Download Framework Overview
            </button>
          </div>
        </section>

        <GridDecoration />
      </main>
    </div>
  );
};

export default Landing;
